import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { API_BASE_URL } from "../../config/apiBase";
import { getEnrollmentStatusLabel, normalizeEnrollmentStatus } from "../../utils/enrollmentStatus";
import { getAuthUserHeader } from "../../utils/userRoles";
import AdminShell from "./AdminShell";
import "../../tutor_v2/TutorDashboardV2.css";

const API = API_BASE_URL;

function statusLabel(kind, value) {
  const token = String(value || "").toUpperCase();
  if (kind === "application") {
    return getEnrollmentStatusLabel(token);
  }
  if (kind === "payment") return token || "NOT PAID";
  if (kind === "account") return token || "NOT ACTIVATED";
  return token || "—";
}

function badgeStyle(kind) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.2,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  };
  const palette = {
    green: { background: "rgba(76, 175, 80, 0.15)", color: "#9ee6a2", borderColor: "rgba(76, 175, 80, 0.35)" },
    amber: { background: "rgba(255, 193, 7, 0.12)", color: "#ffd977", borderColor: "rgba(255, 193, 7, 0.35)" },
    red: { background: "rgba(244, 67, 54, 0.12)", color: "#ffb1aa", borderColor: "rgba(244, 67, 54, 0.35)" },
    slate: { background: "rgba(148, 163, 184, 0.12)", color: "#d5d9e2", borderColor: "rgba(148, 163, 184, 0.25)" },
  };
  return { ...base, ...(palette[kind] || palette.slate) };
}

function statusTone(kind, value) {
  const token = String(value || "").toUpperCase();
  if (kind === "application") {
    if (token === "APPROVED") return "green";
    if (token === "REJECTED") return "red";
    return "amber";
  }
  if (kind === "payment") {
    if (token === "PAID") return "green";
    if (token === "PAYMENT_SUBMITTED") return "amber";
    return "slate";
  }
  if (kind === "account") {
    return token === "ACTIVATED" ? "green" : "slate";
  }
  return "slate";
}

export default function AdminEnrollmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", country: "", programme: "", coupon_code: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function loadEnrollment() {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(`${API}/api/admin/enrollments/${encodeURIComponent(id)}`, {
          cache: "no-store",
          headers: {
            "X-User": getAuthUserHeader(),
          },
        });

        if (!res.ok) {
          const payload = await res.json().catch(() => null);
          throw new Error(
            payload?.detail ||
              payload?.message ||
              `Unable to load enrollment request (${res.status}).`
          );
        }

        const payload = await res.json();

        if (active && mountedRef.current) {
          setItem(payload?.item || null);
          setForm({
            full_name: payload?.item?.full_name || "",
            email: payload?.item?.email || "",
            phone: payload?.item?.phone || payload?.item?.contact || payload?.item?.whatsapp || "",
            country: payload?.item?.country || "",
            programme: payload?.item?.programme || payload?.item?.course || "",
            coupon_code: payload?.item?.coupon_code || "",
          });
        }
      } catch (error) {
        if (!active || !mountedRef.current) {
          return;
        }

        console.error("Failed to load enrollment request:", error);

        if (active && mountedRef.current) {
          setError(error instanceof Error ? error.message : "Unable to load enrollment request.");
          setItem(null);
        }
      } finally {
        if (active && mountedRef.current) {
          setLoading(false);
        }
      }
    }

    void loadEnrollment();

    return () => {
      active = false;
    };
  }, [id]);

  const runAction = async (action, options = {}) => {
    if (!id || busy) return;
    if (options.confirm && !window.confirm(options.confirm)) return;
    try {
      setBusy(action);
      const res = await fetch(`${API}/api/admin/enrollments/${encodeURIComponent(id)}/${action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User": getAuthUserHeader(),
        },
        body: "{}",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Action failed");
      if (mountedRef.current) {
        setLoading(true);
        setError("");
      }
      const refreshed = await fetch(`${API}/api/admin/enrollments/${encodeURIComponent(id)}`, {
        cache: "no-store",
        headers: {
          "X-User": getAuthUserHeader(),
        },
      });
      const payload = await refreshed.json().catch(() => null);
      if (!refreshed.ok) throw new Error((payload && payload.detail) || "Unable to refresh enrollment request.");
      if (mountedRef.current) {
        setItem(payload?.item || null);
        setForm({
          full_name: payload?.item?.full_name || "",
          email: payload?.item?.email || "",
          phone: payload?.item?.phone || payload?.item?.contact || payload?.item?.whatsapp || "",
          country: payload?.item?.country || "",
          programme: payload?.item?.programme || payload?.item?.course || "",
          coupon_code: payload?.item?.coupon_code || "",
        });
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        window.alert(err?.message || "Action failed");
      }
    } finally {
      if (mountedRef.current) {
        setBusy("");
        setLoading(false);
      }
    }
  };

  const saveEnrollment = async () => {
    if (!id || busy) return;
    try {
      setBusy("save");
      const res = await fetch(`${API}/api/admin/enrollments/${encodeURIComponent(id)}/edit`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-User": getAuthUserHeader(),
        },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Save failed");
      setError("");
      setLoading(true);
      const refreshed = await fetch(`${API}/api/admin/enrollments/${encodeURIComponent(id)}`, {
        cache: "no-store",
        headers: { "X-User": getAuthUserHeader() },
      });
      const payload = await refreshed.json().catch(() => null);
      if (!refreshed.ok) throw new Error((payload && payload.detail) || "Unable to refresh enrollment request.");
      if (mountedRef.current) {
        setItem(payload?.item || null);
        setForm({
          full_name: payload?.item?.full_name || "",
          email: payload?.item?.email || "",
          phone: payload?.item?.phone || payload?.item?.contact || payload?.item?.whatsapp || "",
          country: payload?.item?.country || "",
          programme: payload?.item?.programme || payload?.item?.course || "",
          coupon_code: payload?.item?.coupon_code || "",
        });
      }
    } catch (err) {
      window.alert(err?.message || "Save failed");
    } finally {
      if (mountedRef.current) {
        setBusy("");
        setLoading(false);
      }
    }
  };

  const app = normalizeEnrollmentStatus(item?.application_status);
  const pay = String(item?.payment_status || "").toUpperCase();
  const account = String(item?.account_status || "").toUpperCase();
  const canApprove = app !== "APPROVED" && app !== "REJECTED" && account !== "ACTIVATED";
  const canReject = app !== "REJECTED" && account !== "ACTIVATED";
  const canMarkSubmitted = pay !== "PAID" && account !== "ACTIVATED" && app !== "REJECTED";
  const canMarkPaid = pay !== "PAID" && account !== "ACTIVATED" && app !== "REJECTED";

  const fields = [
    ["Full name", item?.full_name],
    ["Email", item?.email],
    ["Phone", item?.phone || item?.contact || item?.whatsapp],
    ["Country", item?.country],
    ["Programme", item?.programme || item?.course],
    ["Message", item?.message],
    ["Submitted date", item?.submitted_date ? new Date(item.submitted_date).toLocaleString() : ""],
    ["Linked student ID", item?.linked_student_id],
    [
      "Linked user ID",
      item?.linked_user_id ? (
        <button
          type="button"
          onClick={() => navigate(`/admin/users/${item.linked_user_id}`)}
          style={{
            padding: 0,
            border: 0,
            background: "transparent",
            color: "#9bd0ff",
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          {item.linked_user_id}
        </button>
      ) : (
        "—"
      ),
    ],
  ];

  return (
    <AdminShell title="Enrollment Request" subtitle="Admin review and activation workflow.">
      <section className="td-card td-header reveal" style={{ "--delay": ".05s" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <h1 className="td-title">Enrollment Request</h1>
            <p className="td-sub">Admin review and activation workflow.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/admin/enrollments")}
            style={{
              padding: "9px 12px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,.12)",
              background: "rgba(255,255,255,.03)",
              color: "inherit",
              cursor: "pointer",
            }}
          >
            Back to list
          </button>
        </div>
      </section>

      {loading ? <div className="td-loading">Loading enrollment…</div> : null}
      {error ? <div className="td-empty" style={{ color: "#ffb1aa" }}>{error}</div> : null}

      {!loading && !error && item ? (
        <>
          <section className="td-card reveal" style={{ "--delay": ".07s", padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 800, marginBottom: 12 }}>Edit Request</div>
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              {[
                ["full_name", "Name"],
                ["email", "Email"],
                ["phone", "Phone"],
                ["country", "Country"],
                ["programme", "Programme"],
                ["coupon_code", "Coupon code"],
              ].map(([field, label]) => (
                <label key={field} style={{ display: "grid", gap: 6 }}>
                  <span style={{ fontSize: 12, color: "var(--muted, #b8b3aa)" }}>{label}</span>
                  <input
                    value={form[field]}
                    onChange={(event) => setForm({ ...form, [field]: event.target.value })}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,.12)",
                      background: "rgba(8,10,14,.65)",
                      color: "#e7e3dc",
                    }}
                  />
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
              <button
                type="button"
                onClick={saveEnrollment}
                disabled={busy === "save"}
                style={actionStyle("primary", busy === "save")}
              >
                {busy === "save" ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </section>

          <section className="td-card reveal" style={{ "--delay": ".08s", padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
              <span style={badgeStyle(statusTone("application", app))}>{statusLabel("application", app)}</span>
              <span style={badgeStyle(statusTone("payment", pay))}>{pay === "PAID" ? "PAID" : "NOT PAID"}</span>
              <span style={badgeStyle(statusTone("account", account))}>{account === "ACTIVATED" ? "ACTIVATED" : "NOT ACTIVATED"}</span>
            </div>

            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              {fields.map(([label, value]) => (
                <div key={label} style={{ padding: 12, borderRadius: 12, background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.06)" }}>
                  <div style={{ fontSize: 12, color: "var(--muted, #b8b3aa)", marginBottom: 6 }}>{label}</div>
                  <div style={{ fontWeight: 600, whiteSpace: "pre-wrap" }}>{value || "—"}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="td-card reveal" style={{ "--delay": ".1s", padding: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              <button
                type="button"
                onClick={() => runAction("approve")}
                disabled={!canApprove || Boolean(busy)}
                style={actionStyle("primary", !canApprove || Boolean(busy))}
              >
                {busy === "approve" ? "Approving..." : "Approve Application"}
              </button>
              <button
                type="button"
                onClick={() => runAction("reject", { confirm: "Reject this enrollment request?" })}
                disabled={!canReject || Boolean(busy)}
                style={actionStyle("danger", !canReject || Boolean(busy))}
              >
                {busy === "reject" ? "Rejecting..." : "Reject Application"}
              </button>
              <button
                type="button"
                onClick={() => runAction("payment-submitted")}
                disabled={!canMarkSubmitted || Boolean(busy)}
                style={actionStyle("secondary", !canMarkSubmitted || Boolean(busy))}
              >
                {busy === "payment-submitted" ? "Updating..." : "Mark Payment Submitted"}
              </button>
              <button
                type="button"
                onClick={() => runAction("payment-paid")}
                disabled={!canMarkPaid || Boolean(busy)}
                style={actionStyle("secondary", !canMarkPaid || Boolean(busy))}
                >
                {busy === "payment-paid" ? "Updating..." : "Mark Payment Paid"}
              </button>
            </div>
            {account !== "ACTIVATED" ? (
              <div style={{ marginTop: 12, color: "var(--muted, #b8b3aa)" }}>
                Activation happens automatically after approval and payment are both complete.
              </div>
            ) : null}
          </section>
        </>
      ) : null}
    </AdminShell>
  );
}

function actionStyle(kind, disabled) {
  const base = {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,.12)",
    color: "#fff",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
  };
  if (kind === "danger") return { ...base, background: "rgba(244, 67, 54, .16)" };
  if (kind === "secondary") return { ...base, background: "rgba(255,255,255,.03)" };
  return { ...base, background: "rgba(201,165,106,.18)" };
}
